/**
 * Helpers compartidos del Portal del Estudio Contable.
 *  - Badges de semáforo (F29/F22) y estado de nómina, con tokens semánticos.
 *  - `useOpenTenant`: salta a la empresa cliente (cross-subdominio, SSO por cookies).
 */
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ConfigBadge, type BadgeConfig } from '@/components/Badge'
import { useAuthStore } from '@/store/authStore'
import { buildTenantSwitchUrl } from '@/lib/tenantNav'
import type { Semaforo, SemaforoEstado, PayrollStatus } from '@/modules/firm/hooks/useFirm'

// ─── Semáforo tributario (F29/F22) ────────────────────────────────────────────

const SEMAFORO_CFG: BadgeConfig<SemaforoEstado> = {
  presentado: { label: 'Presentado', variant: 'emerald' },
  pagado: { label: 'Pagado', variant: 'teal' },
  pendiente: { label: 'Pendiente', variant: 'amber' },
  vencido: { label: 'Vencido', variant: 'red' },
  sin_pago: { label: 'Sin movimiento', variant: 'slate' },
}

export function SemaforoBadge({ s }: { s?: Semaforo | null }) {
  if (!s) return <span className="text-content-subtle text-xs">—</span>
  return <ConfigBadge value={s.estado} config={SEMAFORO_CFG} />
}

// ─── Estado de nómina del mes ──────────────────────────────────────────────────

const PAYROLL_CFG: BadgeConfig<PayrollStatus> = {
  no_existe: { label: 'Sin iniciar', variant: 'slate' },
  draft: { label: 'Borrador', variant: 'amber' },
  calculated: { label: 'Calculada', variant: 'blue' },
  approved: { label: 'Aprobada', variant: 'teal' },
  paid: { label: 'Pagada', variant: 'emerald' },
  closed: { label: 'Cerrada', variant: 'emerald' },
  error: { label: 'Error', variant: 'red' },
}

export function PayrollBadge({ status }: { status?: PayrollStatus | null }) {
  if (!status) return <span className="text-content-subtle text-xs">—</span>
  return <ConfigBadge value={status} config={PAYROLL_CFG} />
}

// ─── Formato de montos (CLP, enteros) ─────────────────────────────────────────

export function formatCLP(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '—'
  return new Intl.NumberFormat('es-CL', {
    style: 'currency', currency: 'CLP', maximumFractionDigits: 0,
  }).format(num)
}

export function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ─── Navegación a la empresa cliente ──────────────────────────────────────────

/**
 * Salta a una empresa cliente. Solo es posible si el usuario tiene acceso (la
 * empresa está en `session.tenants`); el firm_admin ve métricas de clientes a los
 * que NO tiene `TenantMembership`, pero para "entrar" necesita acceso real.
 */
export function useOpenTenant() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { tenants, activeTenantSchema, setActiveTenant } = useAuthStore()

  const canOpen = (schema: string) => tenants.some((t) => t.schema === schema)

  const openTenant = (schema: string) => {
    const target = tenants.find((t) => t.schema === schema)
    if (!target) return
    if (schema === activeTenantSchema) {
      navigate('/dashboard')
      return
    }
    qc.clear()
    setActiveTenant(schema)
    const url = buildTenantSwitchUrl(target, tenants)
    if (url) window.location.href = url // cross-subdominio (SSO vía cookies)
    else navigate('/dashboard', { replace: true })
  }

  return { openTenant, canOpen }
}
